import {
  Stack,
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  TextField,
  List,
  ListItemButton,
  ListItemIcon,
  Checkbox,
  ListItem,
  ListItemText,
  DialogActions,
  CircularProgress,
  Chip,
  SvgIcon,
} from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import EmailIcon from '@mui/icons-material/Email';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import PercentIcon from '@mui/icons-material/Percent';
import {
  AppFormikDatePicker,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { useEffect, useMemo, useState } from 'react';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { ManageEmployeesService } from 'services/ManageEmployeesService';
import DebouncedSearch from 'shared/components/DebouncedSearch';
import { Formik, FormikHelpers } from 'formik';
import { PoliciesService } from 'services';
import { Utils } from 'shared/helpers';
import DateFnsAdapter from '@date-io/date-fns';
import DeleteIcon from '@mui/icons-material/Delete';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { IEmployeeList } from 'modules/settings/management/manage-employees/components/ManageEmployees';

const dateFns = new DateFnsAdapter();

interface IAcknowledgementProps {
  policyId: string;
}
interface IAck {
  _id: string;
  organizationId: string;
  policyId: string;
  employeeId: string;
  reminders: string;
  status: string;
  assignedDate: string;
  dueDate: string;
  isActive: boolean;
  __v: number;
  createdDate: string;
  updatedDate: string;
  employeeName: string;
  employeeMail: string;
  employeeDepartment: string;
}

const Acknowledgement: React.FC<IAcknowledgementProps> = (props) => {
  const { policyId } = props;

  const [showAssignAck, setShowAssignAck] = useState(false);
  const [employees, setEmployees] = useState<IEmployeeList[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [ackRecords, setAckRecords] = useState<IAck[]>([]);
  const totalAcknowledged = useMemo(
    () => ackRecords.filter((a) => a.status !== 'assigned').length,
    [ackRecords]
  );
  const totalPending = useMemo(
    () => ackRecords.filter((a) => a.status === 'assigned').length,
    [ackRecords]
  );
  const totalPercent = useMemo(() => {
    if (ackRecords.length === 0) {
      return 0;
    }
    return Math.round((100 / ackRecords.length) * totalAcknowledged);
  }, [ackRecords, totalAcknowledged]);

  // API Calls
  const loadEmployees = (): void => {
    const queryString = new URLSearchParams({
      offset: '0',
      limit: '1000',
      search: '',
    }).toString();
    ManageEmployeesService.getAll(queryString).then((response) => {
      const { employee } = response.data.data;
      setEmployees(employee);
    });
  };
  const loadAcknowledgements = (): void => {
    setIsLoadingRecords(true);
    PoliciesService.getAcknowledgements(policyId)
      .then((response) => {
        const { data } = response.data;
        setAckRecords(data);
        console.log(data);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };
  const createAcknowledgements = (payload: string, callback: any): void => {
    PoliciesService.createAcknowledgement(payload)
      .then((response) => {
        const { data } = response.data;
        callback(data);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        callback(false);
      });
  };
  const deleteAcknowledgement = (id: string, callback: any): void => {
    PoliciesService.deleteAcknowledgePolicy(id)
      .then((response) => {
        const { data } = response.data;
        callback(data);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        callback(false);
      });
  };

  // Instance Methods
  const resetAssignmentForm = (): void => {
    setSelectedIds([]);
    setShowAssignAck(false);
  };

  // UI Actions
  const handleToggle = (item: IEmployeeList): void => {
    const currentIndex = selectedIds.indexOf(item._id);
    const newChecked = [...selectedIds];
    if (currentIndex === -1) {
      newChecked.push(item._id);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setSelectedIds(newChecked);
  };

  const actionOnDelete = (ack: IAck): void => {
    deleteAcknowledgement(ack._id, (response: any) => {
      if (typeof response === 'object') {
        loadAcknowledgements();
      }
    });
  };

  const actionOnSubmit = (
    formValues: any,
    formikHelper: FormikHelpers<any>
  ): void => {
    const payload: any = {
      policyId,
      employess: [],
    };
    selectedIds.forEach((id) => {
      payload.employess.push({ dueDate: formValues.dueDate, employeeId: id });
    });

    createAcknowledgements(payload, (response: any) => {
      formikHelper.setSubmitting(false);
      if (typeof response === 'object') {
        resetAssignmentForm();
        loadAcknowledgements();
        formikHelper.resetForm();
      }
    });
  };

  // Hooks
  useEffect(() => {
    loadEmployees();
    loadAcknowledgements();
  }, []);

  const filteredEmployees = useMemo(() => {
    if (searchTerm.trim().length > 0) {
      const search = searchTerm.trim().toLowerCase();
      return employees.filter((e) =>
        `${e.name} ${e.email}`.toLowerCase().includes(search)
      );
    }
    return employees;
  }, [searchTerm, employees]);

  const handleSelectAll = (): void => {
    const filteredIds = filteredEmployees.map((emp) => emp._id);
    const allSelected = filteredIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      // Deselect all filtered employees
      const newSelected = selectedIds.filter((id) => !filteredIds.includes(id));
      setSelectedIds(newSelected);
    } else {
      // Select all filtered employees (merge with existing selections)
      const newSelected = [
        ...selectedIds,
        ...filteredIds.filter((id) => !selectedIds.includes(id)),
      ];
      setSelectedIds(newSelected);
    }
  };

  const isAllSelected = useMemo(() => {
    const filteredIds = filteredEmployees.map((emp) => emp._id);
    return (
      filteredIds.length > 0 &&
      filteredIds.every((id) => selectedIds.includes(id))
    );
  }, [filteredEmployees, selectedIds]);

  // UI Elements
  return (
    <>
      {isLoadingRecords && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoadingRecords && (
        <>
          <Box display="flex" gap={2}>
            <Box flex={1}>
              <Box
                className="status-card"
                display="flex"
                alignItems="center"
                gap={5}
                height="100%"
              >
                <PeopleAltIcon />
                <Box sx={{ overflowWrap: 'anywhere' }}>
                  <Typography variant="h3">Total Assigned</Typography>
                  <Typography variant="h2">{ackRecords.length}</Typography>
                </Box>
              </Box>
            </Box>

            <Box flex={1}>
              <Box
                className="status-card"
                display="flex"
                alignItems="center"
                gap={5}
                height="100%"
              >
                <EmailIcon />
                <Box sx={{ overflowWrap: 'anywhere' }}>
                  <Typography variant="h3">Acknowledged</Typography>
                  <Typography variant="h2">{totalAcknowledged}</Typography>
                </Box>
              </Box>
            </Box>

            <Box flex={1}>
              <Box
                className="status-card"
                display="flex"
                alignItems="center"
                gap={5}
                height="100%"
              >
                <InsertInvitationIcon />
                <Box sx={{ overflowWrap: 'anywhere' }}>
                  <Typography variant="h3">Pending</Typography>
                  <Typography variant="h2">{totalPending}</Typography>
                </Box>
              </Box>
            </Box>

            <Box flex={1}>
              <Box
                className="status-card"
                display="flex"
                alignItems="center"
                gap={5}
                height="100%"
              >
                <PercentIcon />
                <Box sx={{ overflowWrap: 'anywhere' }}>
                  <Typography variant="h3">Completion Rate</Typography>
                  <Typography variant="h2">
                    {Math.ceil(totalPercent)}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box className="app-plain-card" sx={{ marginTop: '40px !important' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              className="title"
            >
              <Typography variant="h4">Acknowledgement Assignments</Typography>
              <AppPrimaryButton
                onClick={() => setShowAssignAck(true)}
                title="Assign Employee"
                startIcon={
                  <SvgIcon
                    component={Plus}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                  />
                }
              />
            </Stack>

            <TableContainer>
              <Table className="app-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Assigned Date</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ackRecords.map((row) => (
                    <TableRow key={row._id}>
                      <TableCell>
                        <Typography
                          className="fw-6 f16 text-primary"
                          variant="body1"
                        >
                          {row.employeeName}
                        </Typography>
                        <Typography className="f14" variant="body2">
                          {row.employeeMail}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.employeeDepartment} />
                      </TableCell>
                      <TableCell>
                        {row.status === 'assigned' ? (
                          <Chip label="Pending" />
                        ) : (
                          <Chip label="Acknowledged" color="primary" />
                        )}
                      </TableCell>
                      <TableCell>
                        {dateFns.date(row.createdDate).toDateString()}
                      </TableCell>
                      <TableCell>
                        {row.dueDate
                          ? dateFns.date(row.createdDate).toDateString()
                          : 'No Due Date'}
                      </TableCell>
                      <TableCell>
                        {row.status === 'assigned' && (
                          <IconButton onClick={() => actionOnDelete(row)}>
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {ackRecords.length === 0 && (
                <Box className="empty-content">
                  <Typography variant="h4">
                    No acknowledgements added
                  </Typography>
                </Box>
              )}
            </TableContainer>
          </Box>
        </>
      )}
      <Dialog maxWidth="sm" fullWidth open={showAssignAck}>
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h3">Assign Employees to Policy</Typography>
            <IconButton onClick={() => resetAssignmentForm()}>
              <HighlightOffIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Formik
          initialValues={{ dueDate: '' }}
          onSubmit={(formValues, formik) => {
            actionOnSubmit(formValues, formik);
          }}
        >
          {({ handleSubmit, isSubmitting }) => (
            <>
              <DialogContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <DebouncedSearch
                    size="medium"
                    delay={100}
                    label=""
                    placeholder="Search Employee"
                    onDebounce={(value) => {
                      setSelectedIds([]);
                      setSearchTerm(value);
                      console.log(value);
                    }}
                  />
                  <AppFormikDatePicker
                    disableToday
                    disablePast
                    name="dueDate"
                    outerLabel
                    placeholder="gg/mm/aaaa"
                    renderInput={(props: any) => <TextField {...props} />}
                  />
                </Stack>

                {filteredEmployees.length > 0 && (
                  <ListItem disablePadding>
                    <ListItemButton role={undefined} onClick={handleSelectAll}>
                      <ListItemIcon>
                        <Checkbox
                          color="primary"
                          edge="start"
                          checked={isAllSelected}
                          indeterminate={
                            !isAllSelected &&
                            filteredEmployees.some((emp) =>
                              selectedIds.includes(emp._id)
                            )
                          }
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography className="black f16" fontWeight="bold">
                            Select All - {filteredEmployees.length} Employees
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                )}

                <List>
                  {filteredEmployees.map((item) => (
                    <ListItem key={item._id} disablePadding>
                      <ListItemButton
                        role={undefined}
                        onClick={() => handleToggle(item)}
                      >
                        <ListItemIcon>
                          <Checkbox
                            color="primary"
                            edge="start"
                            checked={selectedIds.includes(item._id)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          color="primary"
                          primary={
                            <Typography className="black f16">
                              {item.name}
                            </Typography>
                          }
                          secondary={
                            <Typography className=" f14">
                              {item.email}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </DialogContent>
              <DialogActions>
                <Stack
                  className="mx-4 mb-4"
                  direction="row"
                  spacing={2}
                  alignItems="center"
                >
                  <AppPrimaryButtonOutlined
                    title="Cancel"
                    className="btn-small"
                    onClick={() => {
                      resetAssignmentForm();
                    }}
                  />
                  <AppPrimaryButton
                    isLoading={isSubmitting}
                    onClick={handleSubmit}
                    disabled={selectedIds.length === 0}
                    title={`Assign ${selectedIds.length} employees`}
                    className="btn-small"
                  />
                </Stack>
              </DialogActions>
            </>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default Acknowledgement;
